/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { FormattedHTMLMessage, FormattedMessage } from 'js/lib/coursera.react-intl';
import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Grid, Typography2 } from '@coursera/cds-core';
import { CheckIcon, WarningIcon } from '@coursera/cds-icons';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { PrivateSessionDrawerContext } from 'bundles/enterprise-admin-drawers/components/PrivateSessionEditDrawerContent';
import { DrawerWarning } from 'bundles/enterprise-admin-drawers/components/components/DrawerInfoBoxes';
import type { CourseSession } from 'bundles/enterprise-admin-drawers/components/types';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import { Spacer } from 'bundles/enterprise-ui/components';

import _t from 'i18n!nls/admin-dashboard';

type Props = {
  selectedSession?: CourseSession;
  migrateLearners: boolean;
  hasUpdatedAvailability: boolean;
  isAlwaysAvailable: boolean;
  hasUpdatedName: boolean;
  isSelectedForCredit?: boolean;
  isSelectedToRequireAllItemCompletion?: boolean;
  hasUpdatedCreditStatus: boolean;
  hasUpdatedItemCompletionStatus: boolean;
  hasUpdatedAutoEnroll: boolean;
  autoEnroll: boolean;
  // override above boolean checks and show all data
  showDefaults: boolean;
  hasConflictingS12nConfig?: boolean;
};

const ChangeItem: React.FC<{ text: string | React.ReactNode }> = ({ text }) => {
  return (
    <Grid container direction="row" wrap="nowrap" spacing={12}>
      <Grid item>
        <CheckIcon size="medium" color="support" />
      </Grid>
      <Grid item>
        <Typography2 component="p">{text}</Typography2>
      </Grid>
    </Grid>
  );
};

const PrivateSessionEditDrawerStep3: React.FC<Props> = ({
  hasUpdatedAvailability,
  isAlwaysAvailable,
  selectedSession,
  migrateLearners,
  hasUpdatedName,
  hasUpdatedCreditStatus,
  hasUpdatedItemCompletionStatus,
  isSelectedForCredit,
  isSelectedToRequireAllItemCompletion,
  hasUpdatedAutoEnroll,
  autoEnroll,
  showDefaults,
  hasConflictingS12nConfig,
}) => {
  const { isS12n, isProfessionalCertificate } = useContext(PrivateSessionDrawerContext);

  const getSelectedForCreditText = () => {
    if (isSelectedForCredit && isS12n) {
      return isProfessionalCertificate
        ? _t('All courses in this Professional Certificate will be offered for credit')
        : _t('All courses in this Specialization will be offered for credit');
    } else if (isSelectedForCredit) {
      return _t('This course will be offered for credit');
    } else if (isS12n) {
      return isProfessionalCertificate
        ? _t('All courses in this Professional Certificate will not be offered for credit')
        : _t('All courses in this Specialization will not be offered for credit');
    } else {
      return _t('This course will not be offered for credit');
    }
  };

  const getItemCompletionRequirementText = () => {
    if (isSelectedForCredit && isSelectedToRequireAllItemCompletion) {
      if (isS12n) {
        return _t(' All course content will be required to complete the Specialization');
      }
      return _t('All content in this course will be required to complete the course');
    } else {
      return _t('All content in this course will not be required to complete the course');
    }
  };

  const getAutoEnrollmentText = () => {
    if (autoEnroll && isS12n) {
      return isProfessionalCertificate
        ? _t(
            'Automatic enrollment is enabled for all courses in this Professional Certificate for all existing and future learners in this learning program'
          )
        : _t(
            'Automatic enrollment is enabled for all courses in this Specialization for all existing and future learners in this learning program'
          );
    } else if (autoEnroll) {
      return (
        <FormattedMessage
          message={_t(
            'Automatic enrollment is enabled for this course for all existing and future learners in this learning program {availability}'
          )}
          availability={
            !isAlwaysAvailable && selectedSession?.endsAt
              ? _t(`on or before #{date}`, {
                  date: formatDateTimeDisplay(selectedSession.endsAt, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY),
                })
              : ''
          }
        />
      );
    } else if (isS12n) {
      return isProfessionalCertificate
        ? _t(
            'Automatic enrollment is disabled for all courses in this Professional Certificate for all future learners in this learning program'
          )
        : _t(
            'Automatic enrollment is disabled for all courses in this Specialization for all future learners in this learning program'
          );
    } else {
      return _t('Automatic enrollment is disabled for this course for all future learners in this learning program');
    }
  };

  const renderApplyChangesWarning = () => {
    if (isS12n) {
      return (
        <>
          <DrawerWarning
            theme="blue"
            title={
              isProfessionalCertificate
                ? _t(
                    'Applying these changes will overwrite any existing configurations in all courses in this Professional Certificate'
                  )
                : _t(
                    'Applying these changes will overwrite any existing configurations in all courses in this Specialization'
                  )
            }
            text={
              isProfessionalCertificate
                ? _t(
                    "Any changes to this Professional Certificate's availability and enrollment settings will be reflected in all its instances in this Learning program, including all the curated collections in which it appears."
                  )
                : _t(
                    'Any changes to this Specialization’s availability and enrollment settings will be reflected in all its instances in this Learning program, including all the curated collections in which it appears.'
                  )
            }
          />
          {hasConflictingS12nConfig && (
            <>
              <Spacer size={24} />
              <DrawerWarning
                theme="yellow"
                title={
                  isProfessionalCertificate
                    ? _t(
                        'Applying these changes will affect the configuration of one or more courses in other Professional Certificates'
                      )
                    : _t(
                        'Applying these changes will affect the configuration of one or more courses in other Specializations'
                      )
                }
                text={
                  isProfessionalCertificate
                    ? _t(
                        'One or more courses in this Professional Certificate have been configured as part of another Professional Certificates. Their existing configuration will be overwritten once the changes listed above are applied. You can minimize this effect by using the same availability dates in all your configurations.'
                      )
                    : _t(
                        'One or more courses in this Specialization have been configured as part of another Specialization. Their existing configuration will be overwritten once the changes listed above are applied. You can minimize this effect by using the same availability dates in all your configurations.'
                      )
                }
              />
            </>
          )}
        </>
      );
    } else {
      return (
        <React.Fragment>
          <hr />
          <Grid container direction="row" wrap="nowrap" spacing={12}>
            <Grid item>
              <WarningIcon size="large" />
            </Grid>
            <Grid item>
              <Typography2 variant="subtitleMedium" component="h4">
                {_t('Applying these changes will override the current course settings')}
              </Typography2>
              <Typography2 component="p">
                {_t(
                  "Any changes to this course's availability and enrollment settings will be reflected in all instances of the course in this Learning program, including all the curated collections in which the course appears."
                )}
              </Typography2>
            </Grid>
          </Grid>
        </React.Fragment>
      );
    }
  };

  return (
    <div>
      <Typography2 variant="subtitleMedium" component="h4">
        {_t('Change summary')}
      </Typography2>
      <Spacer size={12} />
      {(showDefaults || hasUpdatedCreditStatus) && <ChangeItem text={getSelectedForCreditText()} />}
      {(showDefaults || hasUpdatedItemCompletionStatus) && <ChangeItem text={getItemCompletionRequirementText()} />}
      {(hasUpdatedAvailability || showDefaults) &&
        !isAlwaysAvailable &&
        selectedSession?.startsAt &&
        selectedSession?.endsAt && (
          <ChangeItem
            text={
              <FormattedHTMLMessage
                message={_t('New availability: Limited availability - {startDate} till {endDate}')}
                startDate={formatDateTimeDisplay(selectedSession.startsAt, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY)}
                endDate={formatDateTimeDisplay(selectedSession.endsAt, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY)}
              />
            }
          />
        )}
      {((hasUpdatedAvailability && isAlwaysAvailable) || showDefaults) && (
        <ChangeItem text={_t('New schedule: Always available')} />
      )}
      {(hasUpdatedName || showDefaults) && (
        <ChangeItem
          text={
            <FormattedHTMLMessage
              message={_t('New availability period name: {name}')}
              name={selectedSession?.name || <em>{_t('left blank')}</em>}
            />
          }
        />
      )}
      {(showDefaults || hasUpdatedAutoEnroll) && <ChangeItem text={getAutoEnrollmentText()} />}
      {showDefaults ||
        (hasUpdatedAvailability && (
          <ChangeItem
            text={
              migrateLearners
                ? _t('Enrolled learners will be migrated to the selected availability')
                : _t('Enrolled learners will not be migrated to the selected availability')
            }
          />
        ))}
      {(hasUpdatedAvailability || hasUpdatedAutoEnroll || hasUpdatedCreditStatus || hasUpdatedItemCompletionStatus) && (
        <div>
          <Spacer size={64} />
          {renderApplyChangesWarning()}
          <Spacer size={48} />
        </div>
      )}
      {hasUpdatedAutoEnroll && autoEnroll && enterpriseAdminUser.isC4CBOrg && (
        <React.Fragment>
          <hr />
          <Grid container direction="row" wrap="nowrap" spacing={12}>
            <Grid item>
              <WarningIcon size="large" />
            </Grid>
            <Grid item>
              <Typography2 variant="subtitleMedium" component="h4">
                {_t('Automatic enrollments are restricted by enrollment limits')}
              </Typography2>
              <Typography2 component="p">
                {_t(
                  'Coursera for Campus Basic accounts have an enrollment limit of 1 course per year. Automatic enrollments will not work on learners who reached that limit.'
                )}
              </Typography2>
            </Grid>
          </Grid>
        </React.Fragment>
      )}
    </div>
  );
};

export default PrivateSessionEditDrawerStep3;
